import { Add } from "@mui/icons-material";
import { Box, BoxProps, Skeleton, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useHistory } from "react-router-dom";

import { SparklesIcon } from "@/Components/Icons/SparklesIcons";
import { Button } from "@/Components/v2/atoms/Button";
import { BetaPill } from "@/Components/v2/atoms/Pill";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { DRAWER_BREAKPOINT, DRAWER_COLLAPSED_WIDTH_PX } from "@/Utils/GlobalStyle";

import { ChatV1 } from "../../api/schema/openapi";
import { useChat } from "../../context/ChatContext";
import { useChatHistory } from "../../hooks/queries/useChatHistory";

const PreviousChatTextMaxChars = 65;

export const ChatHistoryNavbar: React.FC<BoxProps> = (props) => {
  const { data: chats, isLoading } = useChatHistory();
  const { clearChat, setCurrentStreamId, activeStreams } = useChat();
  const history = useHistory();
  const isLargerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up(DRAWER_BREAKPOINT));
  const [isExpanded, setIsExpanded] = React.useState(false);

  const theme = useTheme();

  const handleStartNewChat = () => {
    clearChat();
    setCurrentStreamId("");
    history.push("/maite-v2");
  };

  const handlePreviousChatClick = (id: string) => () => {
    history.push(`/maite-v2/${id}`);
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const filteredChats = chats?.filter((chat) => {
    if (activeStreams?.[chat.id]) {
      return true;
    }

    if (chat.initialQuery && chat.initialQuery !== "") {
      return true;
    }
    return false;
  });

  const maxVisibleChats = 5;
  const shouldShowExpandButton = filteredChats && filteredChats.length > maxVisibleChats;
  const visibleChats =
    isExpanded || !shouldShowExpandButton
      ? filteredChats
      : filteredChats?.slice(0, maxVisibleChats);

  return (
    <Box
      maxHeight="100vh"
      minWidth="300px"
      maxWidth="300px"
      p={1.5}
      ml={isLargerScreen ? 0 : `${DRAWER_COLLAPSED_WIDTH_PX}px`}
      zIndex={2}
      display="flex"
      flexDirection="column"
      alignItems="start"
      {...props}
    >
      <Box display="flex" alignItems="center" sx={{ pl: 1 }}>
        <SparklesIcon
          sx={{
            mr: 1.5,
          }}
          fill={theme.palette.primary.contrastText}
        />
        <Typography variant="h2" py={1.5}>
          MAITE
        </Typography>
        <BetaPill />
      </Box>

      <Button
        onClick={handleStartNewChat}
        variant="text"
        color="primary"
        disabled={isLoading}
        startIcon={<Add />}
        sx={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 600,
          fontSize: "14px",
          py: 0.5,
          "&": {
            textTransform: "capitalize",
          },
        }}
      >
        New Chat
      </Button>

      <Typography variant="body1" my={1} sx={{ fontWeight: 600, pl: 1 }}>
        Recent Chats:
      </Typography>

      {isLoading ? (
        <>
          <Skeleton
            variant="rounded"
            sx={{ borderRadius: 3, mb: 0.5, height: "56px", width: "100%" }}
          />
          <Skeleton
            variant="rounded"
            sx={{ borderRadius: 3, mb: 0.5, height: "56px", width: "100%" }}
          />
        </>
      ) : (
        <Box
          flex={1}
          sx={{
            overflowY: "auto",
          }}
        >
          {visibleChats && visibleChats.length > 0 ? (
            visibleChats.map((chat: ChatV1) => (
              <Button
                key={chat.id}
                onClick={handlePreviousChatClick(chat.id)}
                fullWidth
                sx={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "16px",
                  justifyContent: "flex-start",
                  paddingX: "0px",
                  pl: 1,
                  "&": {
                    textAlign: "left",
                    textTransform: "none",
                  },
                  alignItems: "center",
                }}
                color={"primary"}
                variant="text"
              >
                {activeStreams?.[chat.id]?.isStreaming && (
                  <CircularProgress size={12} sx={{ color: "primary.main", mr: 1 }} />
                )}
                <Box
                  component="span"
                  sx={{
                    opacity: activeStreams?.[chat.id]?.isStreaming ? 0.5 : 1,
                  }}
                >
                  {(() => {
                    const text =
                      chat.initialQuery && chat.initialQuery.length > PreviousChatTextMaxChars
                        ? `${chat.initialQuery.slice(0, PreviousChatTextMaxChars)}...`
                        : chat.initialQuery;
                    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                  })()}
                </Box>
              </Button>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", fontSize: "14px" }}
            >
              No past chats yet. Start a conversation!
            </Typography>
          )}

          {shouldShowExpandButton && (
            <Button
              onClick={handleToggleExpanded}
              variant="text"
              color="primary"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                justifyContent: "flex-start",
                color: "#2E90E1",
                pl: 1,
                "&": {
                  textAlign: "left",
                  textTransform: "none",
                },
              }}
            >
              {isExpanded
                ? "Show less"
                : `See older chats (${filteredChats.length - maxVisibleChats} more)`}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
